import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "../translations";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { API_BASE } from '../config';
import { Modal } from '../components/ui/Modal';
import { Banner } from '../components/ui/Banner';
import { scrollToTop } from '../utils/scrollToTop';
import { panelTopHairline } from '../ui/common';

// SVG Icons
const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: clamp(12px, 2vw, 32px);
  background: var(--panel-bg);
  border-radius: 16px;
  box-shadow: 0 8px 32px var(--shadow-color);
  color: var(--text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  position: relative;
  overflow: hidden;
  min-height: calc(100vh - 48px);

  ${panelTopHairline}

  @media (min-width: 1200px) {
    max-width: 1000px;
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
    min-height: auto;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
`;

const BackButton = styled.button`
  background: var(--input-bg);
  color: var(--text);
  border: 2px solid var(--input-border);
  padding: 10px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: var(--table-row-hover);
    border-color: var(--primary);
    color: var(--primary);
    transform: translateY(-1px);
  }
`;

const TitleSection = styled.div`
  text-align: center;
  flex: 1;
`;

const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 2rem;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.025em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  /* czytelny w dark mode */
`;

const Panel = styled.div`
  background: var(--card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${slideIn} 0.6s ease-out;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px var(--shadow-color);
    border-color: var(--input-border);
  }
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  align-items: center;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.div`
  min-width: 160px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Value = styled.div`
  flex: 1;
  font-size: 1rem;
  background: var(--input-bg);
  padding: 12px 16px;
  border-radius: 12px;
  border: 2px solid var(--input-border);
  color: var(--text);
  font-weight: 500;
`;

const Input = styled.input`
  flex: 1;
  padding: 12px 16px;
  border-radius: 12px;
  border: 2px solid ${props => props['data-error'] ? 'var(--danger)' : 'var(--input-border)'};
  font-size: 1rem;
  background: var(--input-bg);
  color: var(--text);
  transition: all 0.2s ease;
  font-weight: 500;
  
  &:focus {
    outline: none;
    border-color: ${props => props['data-error'] ? 'var(--danger)' : 'var(--primary)'};
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  flex-wrap: wrap;
  
  @media(max-width: 768px) {
    justify-content: stretch;
  }
`;

const PrimaryButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-primary);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media(max-width: 768px) {
    flex: 1;
  }
`;

const SecondaryButton = styled.button`
  background: var(--input-bg);
  color: var(--text);
  border: 2px solid var(--input-border);
  padding: 10px 20px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  
  &:hover:not(:disabled) {
    background: var(--table-row-hover);
    border-color: var(--primary);
    color: var(--primary);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: var(--primary);
`;

// Tresc modala potwierdzenia uprawnien — sam modal to wspolny <Modal>
// z components/ui/Modal.js, tu zostaje tylko wnetrze.
const ModalBody = styled.div`
  margin: 12px 0 18px 0;
  color: var(--text-muted);
  font-size: 0.95rem;
  line-height: 1.4;
`;

export default function UserDetails(){
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editing, setEditing] = useState(location.pathname.endsWith('/edit'));
  const [form, setForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone_number: '',
    isadmin: false
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // admin modal state
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminModalMessage, setAdminModalMessage] = useState('');
  const [pendingAdmin, setPendingAdmin] = useState(null);
  const [adminProcessing, setAdminProcessing] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordError, setAdminPasswordError] = useState(null);
  const [savedPayload, setSavedPayload] = useState(null);

  useEffect(() => {
    setEditing(location.pathname.endsWith('/edit'));
  }, [location.pathname]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/users/${id}`, { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error("Network response was not ok");
        return r.json();
      })
      .then(data => {
        setUser(data || null);
        if (data) {
          setForm({
            firstname: data.firstname || '',
            lastname: data.lastname || '',
            email: data.email || '',
            phone_number: data.phone_number || '',
            isadmin: !!data.isadmin
          });
        }
      })
      .catch(err => { console.error(err); setError(t('userDetails.loadError') || "Cannot load user"); })
      .finally(() => setLoading(false));
  }, [id]);

  const onChange = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setFieldErrors(prev => ({ ...prev, [k]: false }));
    setError(null);
  };

  const getCookie = (name) => {
    if (typeof document === 'undefined') return '';
    const v = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith(name + '='));
    return v ? decodeURIComponent(v.split('=').slice(1).join('=')) : '';
  };

  const onSave = async () => {
    setSaving(true);
    setMsg(null);
    setError(null);
    setFieldErrors({});
    try {
      const payload = {
        firstname: form.firstname,
        lastname: form.lastname,
        email: form.email,
        phone_number: form.phone_number,
        profile_picture: form.profile_picture || null
      };
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/users/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const text = await res.text();
        const lower = (text || '').toLowerCase();
        const errors = {};
        if (/imi|first/.test(lower)) errors.firstname = true;
        if (/nazw|last/.test(lower)) errors.lastname = true;
        if (/email/.test(lower)) errors.email = true;
        if (/telefon|phone/.test(lower)) {
          errors.phone_number = true;
          setError(t('users.invalidPhone'));
        } else {
          setError(text || 'Błąd zapisu');
        }
        setFieldErrors(errors);
        setSaving(false);
        scrollToTop();
        return;
      }

      // jeśli flaga admina się zmieniła — pokaż modal potwierdzenia zamiast window.confirm
      const prevIsAdmin = !!user?.isadmin;
      const newIsAdmin = !!form.isadmin;
      if (prevIsAdmin !== newIsAdmin) {
        setPendingAdmin(newIsAdmin);
        setSavedPayload(payload); // zapamiętaj payload, dokończymy po potwierdzeniu
        setAdminError(null);
        setAdminPassword('');
        setAdminPasswordError(null);
        setAdminModalMessage(
          newIsAdmin
            ? 'Potwierdź nadanie uprawnień administratora temu użytkownikowi. Użytkownik otrzyma dostęp do panelu administracyjnego.'
            : 'Potwierdź odebranie uprawnień administratora. Upewnij się, że nie pozostawiasz systemu bez administratorów.'
        );
        setShowAdminModal(true);
        setSaving(false);
        return;
      }

      // odśwież UI / navigate / success
      setUser(prev => ({ ...prev, ...payload, isadmin: newIsAdmin }));
      setEditing(false);
      setMsg('Zapisano');
      navigate('/users', { replace: true });
    } catch (err) {
      console.error(err);
      setError('Błąd zapisu');
      scrollToTop();
    } finally {
      setSaving(false);
    }
  };

  // potwierdź zmianę admina (wywoływane z modala)
  const confirmAdminChange = async (confirm) => {
    if (!confirm) {
      setShowAdminModal(false);
      setPendingAdmin(null);
      setSavedPayload(null);
      setAdminPassword('');
      setAdminPasswordError(null);
      return;
    }
    if (pendingAdmin === null) return;

    if (!adminPassword) {
      setAdminPasswordError('Wpisz hasło, aby potwierdzić operację.');
      return;
    }

    setAdminProcessing(true);
    setAdminError(null);
    setAdminPasswordError(null);
    try {
      const csrf = getCookie('csrf') || (localStorage.getItem('csrfToken') || '');
      const adminRes = await fetch(`${API_BASE}/api/users/${id}/admin`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        },
        body: JSON.stringify({ isAdmin: pendingAdmin, password: adminPassword })
      });

      if (!adminRes.ok) {
        // spróbuj odczytać odpowiedź JSON z komunikatem
        const bodyText = await adminRes.text().catch(()=>null);
        let parsed = null;
        try { parsed = bodyText ? JSON.parse(bodyText) : null; } catch(_) {}

        if (adminRes.status === 401) {
          setAdminPasswordError('Nieprawidłowe hasło.');
          return;
        }
        // specjalny komunikat gdy backend zabrania odebrania ostatniego admina
        if (adminRes.status === 400 && parsed && /last administrator/i.test(parsed.message || '') ) {
          setAdminError('Nie można odebrać uprawnień - to ostatni administrator w systemie.');
          // pozostaw modal z komunikatem o błędzie (tylko przycisk Zamknij)
          return;
        } else {
          setAdminPasswordError(parsed?.message || bodyText || `Błąd serwera (${adminRes.status})`);
          return;
        }
      }

      // powodzenie — zaktualizuj UI, zamknij modal i nawiguj
      const updated = await adminRes.json().catch(()=>null);
      setUser(prev => ({ ...prev, ...savedPayload, isadmin: !!pendingAdmin }));
      setShowAdminModal(false);
      setPendingAdmin(null);
      setSavedPayload(null);
      setAdminPassword('');
      setAdminPasswordError(null);
      setMsg('Zapisano');
      navigate('/users', { replace: true });
    } catch (e) {
      console.error('confirmAdminChange error', e);
      setAdminPasswordError('Błąd sieci. Spróbuj ponownie.');
    } finally {
      setAdminProcessing(false);
    }
  };

  const onCancel = () => {
    if (user) {
      setForm({
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        email: user.email || '',
        phone_number: user.phone_number || '',
        isadmin: !!user.isadmin
      });
    }
    setEditing(false);
    if (location.pathname.endsWith('/edit')) navigate(`/users/${id}`, { replace: true });
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/users')} title={t('common.back')}>
          <ArrowLeftIcon />
        </BackButton>
        <TitleSection>
          <Title>
            <UserIcon />
            {t('users.title')}
          </Title>
        </TitleSection>
      </Header>

      {loading && <Banner tone="info">{t('common.loading')}</Banner>}
      {error && !user && <Banner tone="danger">{error}</Banner>}
      {msg && <Banner tone="success">{msg}</Banner>}

      {!loading && user && (
        <Panel>
          {error && user && <Banner tone="danger">{error}</Banner>}
          <Row>
            <Label>
              <UserIcon />
              {t('users.firstName')}
            </Label>
            {!editing ? <Value>{user.firstname || ''}</Value> :
              <Input value={form.firstname} onChange={e => onChange('firstname', e.target.value)} />
            }
          </Row>

          <Row>
            <Label>
              <UserIcon />
              {t('users.lastName')}
            </Label>
            {!editing ? <Value>{user.lastname || ''}</Value> :
              <Input value={form.lastname} onChange={e => onChange('lastname', e.target.value)} />
            }
          </Row>

          <Row>
            <Label>
              <MailIcon />
              {t('users.email')}
            </Label>
            {!editing ? <Value>{user.email || ''}</Value> :
              <Input type="email" value={form.email} onChange={e => onChange('email', e.target.value)} />
            }
          </Row>

          <Row>
            <Label>
              <PhoneIcon />
              {t('yourData.phone')}
            </Label>
            {!editing ? <Value>{user.phone_number || ''}</Value> :
              <Input 
                value={form.phone_number} 
                onChange={e => onChange('phone_number', e.target.value.replace(/[^\d+ ]/g,''))}
                data-error={!!fieldErrors.phone_number}
              />
            }
          </Row>

          <Row>
            <Label>
              <CheckIcon />
              {t('users.admin')}
            </Label>
            {!editing ? <Value>{user.isadmin ? (t('common.yes') || 'Tak') : (t('common.no') || 'Nie')}</Value> :
              <CheckboxWrapper>
                <Checkbox
                  type="checkbox"
                  checked={!!form.isadmin}
                  onChange={e => onChange('isadmin', e.target.checked)}
                />
                <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>{t('addUser.adminInfo')}</span>
              </CheckboxWrapper>
            }
          </Row>

          <ButtonGroup>
            {!editing && (
              <PrimaryButton as={Link} to={`/users/${id}/edit`}>
                <EditIcon />
                {t('common.edit')}
              </PrimaryButton>
            )}

            {editing && (
              <>
                <SecondaryButton onClick={onCancel} disabled={saving}>
                  <XIcon />
                  {t('common.cancel')}
                </SecondaryButton>
                <PrimaryButton onClick={onSave} disabled={saving}>
                  <CheckIcon />
                  {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
                </PrimaryButton>
              </>
            )}
          </ButtonGroup>
        </Panel>
      )}

      {!loading && !error && !user && (
        <Panel>
          <Banner tone="danger">{t('users.noUsers')}</Banner>
        </Panel>
      )}

      {/* Modal potwierdzenia zmiany uprawnień administratora */}
      <Modal
        open={showAdminModal}
        onClose={() => { if (!adminProcessing) { setShowAdminModal(false); setAdminError(null); } }}
        dismissOnBackdrop={!adminProcessing}
        title={t('userDetails.adminModalTitle')}
        size="sm"
        footer={
          <>
            {!adminError && (
              <SecondaryButton onClick={() => confirmAdminChange(false)} disabled={adminProcessing}>
                {t('common.cancel')}
              </SecondaryButton>
            )}
            <PrimaryButton
              as="button"
              onClick={() => {
                if (adminError) {
                  // gdy jest błąd (np. ostatni admin) przycisk "Zamknij" tylko zamyka modal
                  setShowAdminModal(false);
                  setAdminError(null);
                  setPendingAdmin(null);
                  setSavedPayload(null);
                } else {
                  confirmAdminChange(true);
                }
              }}
              disabled={adminProcessing}
              style={{ minWidth: 120 }}
            >
              {adminProcessing ? '...' : (adminError ? t('common.close') : t('common.confirm'))}
            </PrimaryButton>
          </>
        }
      >
        <ModalBody>
          {adminError ? (
            <Banner tone="danger" compact>{adminError}</Banner>
          ) : (
            <>
              <div>{adminModalMessage}</div>
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: '0.9rem', marginBottom: 6 }}>
                  {t('users.passwordConfirmLabel')}
                </div>
                <Input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder={t('users.passwordPlaceholder')}
                  autoFocus
                />
                {adminPasswordError && (
                  <div style={{ marginTop: 6 }}><Banner tone="danger" compact>{adminPasswordError}</Banner></div>
                )}
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
      )}
    </Container>
  );
}